/**
 * <cfs-tip>: an "i" button with a short explanation (snippets/cfs-fee-tip.liquid).
 * - Mouse: shows on hover over the button or the bubble.
 * - Touch / click: the button toggles it.
 * - Keyboard: shows while the button has focus; Esc closes it.
 * - A tap or click elsewhere closes it.
 * The bubble is a popover (top layer) placed with fixed coordinates under the button, or above it
 * when there's no room below, and kept inside the screen. Without popover support it is positioned
 * in the page as a fallback.
 */
let count = 0;
const supportsPopover = typeof HTMLElement !== 'undefined' && 'showPopover' in HTMLElement.prototype;

class CfsTip extends HTMLElement {
  #hideTimer = 0;
  #pinned = false; // opened by a tap/click: stays until closed

  connectedCallback() {
    this.button = /** @type {HTMLButtonElement | null} */ (this.querySelector('.cfs-tip__button'));
    this.bubble = /** @type {HTMLElement | null} */ (this.querySelector('.cfs-tip__bubble'));
    if (!this.button || !this.bubble) return;
    this.bubble.id ||= `CfsTip-${++count}`;
    this.button.setAttribute('aria-describedby', this.bubble.id);
    if (!supportsPopover) this.bubble.removeAttribute('popover');

    this.button.addEventListener('click', this.#onClick);
    this.button.addEventListener('focus', this.#onFocus);
    this.button.addEventListener('blur', this.#onBlur);
    for (const el of [this.button, this.bubble]) {
      el.addEventListener('pointerenter', this.#onEnter);
      el.addEventListener('pointerleave', this.#onLeave);
    }
  }

  disconnectedCallback() {
    this.#hide();
  }

  get open() {
    return this.button?.getAttribute('aria-expanded') === 'true';
  }

  /** @param {PointerEvent} event */
  #onEnter = (event) => {
    if (event.pointerType !== 'mouse') return;
    window.clearTimeout(this.#hideTimer);
    this.#show();
  };

  /** @param {PointerEvent} event */
  #onLeave = (event) => {
    if (event.pointerType !== 'mouse' || this.#pinned) return;
    this.#hideTimer = window.setTimeout(() => this.#hide(), 150);
  };

  #onClick = () => {
    if (this.open && this.#pinned) {
      this.#hide();
      return;
    }
    this.#pinned = true;
    this.#show();
  };

  #onFocus = () => {
    if (this.button?.matches(':focus-visible')) this.#show();
  };

  #onBlur = () => {
    if (this.#pinned) return; // opened by a tap: a tap elsewhere closes it (#onOutside)
    this.#hideTimer = window.setTimeout(() => this.#hide(), 100);
  };

  #show() {
    const { button, bubble } = this;
    if (!button || !bubble) return;
    for (const other of document.querySelectorAll('cfs-tip')) if (other !== this) /** @type {any} */ (other).close?.();
    if (!this.open) {
      button.setAttribute('aria-expanded', 'true');
      if (bubble.hasAttribute('popover')) {
        try {
          bubble.showPopover();
        } catch {
          // already showing
        }
      }
      bubble.classList.add('is-open');
      document.addEventListener('pointerdown', this.#onOutside, true);
      document.addEventListener('keydown', this.#onKey, true);
      window.addEventListener('scroll', this.#place, true);
      window.addEventListener('resize', this.#place);
    }
    this.#place();
  }

  close() {
    this.#hide();
  }

  #hide() {
    window.clearTimeout(this.#hideTimer);
    this.#pinned = false;
    const { button, bubble } = this;
    if (!button || !bubble || !this.open) return;
    button.setAttribute('aria-expanded', 'false');
    bubble.classList.remove('is-open');
    if (bubble.hasAttribute('popover')) {
      try {
        bubble.hidePopover();
      } catch {
        // already hidden
      }
    }
    document.removeEventListener('pointerdown', this.#onOutside, true);
    document.removeEventListener('keydown', this.#onKey, true);
    window.removeEventListener('scroll', this.#place, true);
    window.removeEventListener('resize', this.#place);
  }

  /** @param {PointerEvent} event */
  #onOutside = (event) => {
    if (!this.contains(/** @type {Node} */ (event.target))) this.#hide();
  };

  /** @param {KeyboardEvent} event */
  #onKey = (event) => {
    if (event.key === 'Escape') {
      event.stopPropagation(); // close the tip, not the drawer it sits in
      this.#hide();
      this.button?.focus();
    }
  };

  #place = () => {
    const { button, bubble } = this;
    if (!button || !bubble || !this.open) return;
    if (!bubble.hasAttribute('popover')) return; // fallback: CSS places it under the button
    const gap = 8;
    const edge = 8;
    const b = button.getBoundingClientRect();
    const width = Math.min(260, window.innerWidth - edge * 2);
    bubble.style.width = `${width}px`;
    const height = bubble.offsetHeight;
    const below = b.bottom + gap + height <= window.innerHeight - edge;
    const top = below ? b.bottom + gap : Math.max(edge, b.top - gap - height);
    const left = Math.min(Math.max(edge, b.left + b.width / 2 - width / 2), window.innerWidth - width - edge);
    bubble.style.top = `${Math.round(top)}px`;
    bubble.style.left = `${Math.round(left)}px`;
  };
}

if (!customElements.get('cfs-tip')) customElements.define('cfs-tip', CfsTip);
